import type { WidgetConfig } from "@anota/shared";
import { ApiError, getConfig } from "./api";
import { serverBackend } from "./backend";
import { installConsoleCapture } from "./capture/console";
import { installNetworkCapture } from "./capture/network";
import { installTrail } from "./capture/trail";
import { mount, type Mounted } from "./mount";
import { forgetReviewToken, reviewToken } from "./review";
import { loadShotScript, registerShot, type CaptureFn } from "./shot-loader";
import { cacheConfig, cachedConfig, isDenied, rememberDenied } from "./storage";

/**
 * Anota — widget de notas visuales. Se instala con una línea:
 *   <script src="https://<anota>/w.js" data-project="pk_…" async></script>
 * Atributos opcionales: data-lang (es, pt, en), data-launcher (bar, button, icon, tab, hidden: cómo se ve)
 * y data-api (otra dirección para la API).
 * Expone `window.Anota.open()` para abrirlo desde un botón propio del sitio y `window.Anota.openNotes()`
 * para «Tus notas» (las que se dejaron desde este navegador, con su estado y las respuestas).
 * (Para instalarlo con npm, en React o con un envío propio: src/npm/.)
 */

const VERSION = "0.1.0";

interface AnotaGlobal {
  version: string;
  open: () => void;
  openNotes: () => void;
  _registerShot: (fn: CaptureFn) => void;
}

const script = document.currentScript as HTMLScriptElement | null;
// Desde ya: los errores, las peticiones que fallan y los pasos de antes de abrir el widget son lo que más ayuda.
installConsoleCapture();
installNetworkCapture();
installTrail();

const w = window as unknown as { Anota?: AnotaGlobal };
if (!w.Anota) {
  let mounted: Mounted | null = null;
  // Si el sitio llama a Anota.open() u openNotes() mientras aún se carga la configuración, se abre al montarse.
  let openWhenReady: "open" | "openNotes" | null = null;
  const later = (what: "open" | "openNotes") => () => {
    if (mounted) mounted[what]();
    else openWhenReady = what;
  };
  w.Anota = { version: VERSION, open: later("open"), openNotes: later("openNotes"), _registerShot: registerShot };

  const key = script?.dataset.project;
  const base = (script?.dataset.api || (script?.src ? new URL(script.src, location.href).origin : "")).replace(/\/+$/, "");
  const nonce = script?.nonce || undefined;
  const start = async () => {
    const review = reviewToken(key!);
    // Sin enlace de revisión y ya dijo que no hace poco: ni se pregunta.
    if (!review && isDenied(key!)) return;
    const cacheKey = review ? `${key}:${review.slice(-8)}` : key!;
    let config = cachedConfig<WidgetConfig>(cacheKey);
    if (!config) {
      try {
        config = await getConfig(base, key!, review);
        cacheConfig(cacheKey, config);
      } catch (err) {
        // "Solo con enlace" y este visitante no tiene uno (o ya no vale): el widget no aparece, sin avisar.
        if (err instanceof ApiError && err.code === "review_required") {
          if (review) forgetReviewToken(key!);
          else rememberDenied(key!);
          return;
        }
        console.warn(`[Anota] ${err instanceof Error ? err.message : String(err)}`);
        return;
      }
    }
    mounted = mount({
      config,
      backend: serverBackend(base, key!, () => loadShotScript(base, nonce), review),
      lang: script?.dataset.lang,
      nonce,
      launcher: script?.dataset.launcher,
    });
    if (openWhenReady) {
      mounted[openWhenReady]();
      openWhenReady = null;
    }
  };

  if (!key || !base) console.warn("[Anota] Falta data-project en el <script> del widget.");
  else if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => void start(), { once: true });
  else void start();
}
